/** Drawing the Daily Dig: today's cave, your day so far or the online board, the last run. */

import type { CaveDef } from '../../caves/types';
import type { BoardEntry } from '../../platform/leaderboard';
import type { DailyRecord } from '../../platform/records';
import type { Screen } from '../../render/screen';
import type { DailyRun } from '../scene';
import { drawPanel } from '../kit/chrome';
import type { Rect } from '../kit/menuModel';
import { drawSpinner } from '../kit/spinner';
import { commas, seconds, wrap } from '../kit/text';
import { drawCaveThumb } from '../kit/thumbs';
import { drawUncover } from '../kit/uncover';
import { ACCENT, BAD, blink, cycle, FAINT, GOOD, HEADING, INK, MUTED, RAINBOW, SELECTED } from '../kit/theme';
import { boardLine } from './dailyModel';

export const THUMB_PANEL: Rect = { x: 8, y: 32, w: 126, h: 72 };
const THUMB: Rect = { x: 11, y: 35, w: 120, h: 66 };
const RIGHT_X = 150;

/** The cave: picture (uncovering as the screen opens) and name. */
export function drawCave(screen: Screen, def: CaveDef, ms: number): void {
  drawPanel(screen, THUMB_PANEL);
  drawCaveThumb(screen, def, 1, THUMB);
  drawUncover(screen, THUMB, ms - 150, 7, def.colors, 8);
  const cx = THUMB_PANEL.x + THUMB_PANEL.w / 2;
  screen.text(def.name, cx, 110, SELECTED, 'center');
  screen.text('ONE CAVE A DAY,', cx, 122, FAINT, 'center');
  screen.text('THE SAME FOR ALL', cx, 131, FAINT, 'center');
}

/** Your day so far, big, and the cave's hint. */
export function drawToday(screen: Screen, r: DailyRecord, hint: string): void {
  screen.text('YOUR BEST TODAY', RIGHT_X, 34, HEADING);
  if (r.won) {
    const text = commas(r.best);
    screen.text(text, RIGHT_X + 1, 49, 0, 'left', 2);
    screen.text(text, RIGHT_X, 48, INK, 'left', 2);
    screen.text(`◆${r.diamonds}`, RIGHT_X, 70, SELECTED);
    screen.text(`⌛${seconds(r.bestTime)}`, RIGHT_X + 48, 70, ACCENT);
  } else {
    screen.text(r.attempts > 0 ? 'NO EXIT YET' : 'NOT DUG YET', RIGHT_X, 50, FAINT);
  }
  screen.text('ATTEMPTS', RIGHT_X, 86, MUTED);
  screen.text(String(r.attempts), RIGHT_X + 80, 86, INK);
  wrap(hint, 20).slice(0, 4).forEach((line, i) => screen.text(line, RIGHT_X, 102 + i * 9, MUTED));
}

/** The online board, top ten; `mine` is the line to flash (just sent). */
export function drawBoard(screen: Screen, board: readonly BoardEntry[], mine: BoardEntry | null, ms: number): void {
  screen.text("TODAY'S BOARD", RIGHT_X, 34, HEADING);
  if (board.length === 0) {
    screen.text('NOBODY YET.', RIGHT_X, 50, FAINT);
    screen.text('BE THE FIRST!', RIGHT_X, 60, MUTED);
    return;
  }
  board.slice(0, 10).forEach((e, i) => {
    const me = mine !== null && e.initials === mine.initials && e.score === mine.score && e.seconds === mine.seconds;
    const colour = me ? cycle(RAINBOW, ms, 70) : i === 0 ? SELECTED : INK;
    screen.text(boardLine(i + 1, e), RIGHT_X, 46 + i * 9, colour);
  });
}

/** The run just played. */
export function drawResult(screen: Screen, run: DailyRun): void {
  const parts = `◆${run.diamonds}  ⌛${seconds(run.seconds)}  ${commas(run.score)}`;
  if (run.won) screen.text(`MADE IT!  ${parts}`, 160, 146, GOOD, 'center');
  else screen.text(`NO EXIT THIS TIME  ◆${run.diamonds}  ${commas(run.score)}`, 160, 146, BAD, 'center');
}

/** The share line in its box, with a note after a copy. */
export function drawShare(screen: Screen, line: string, note: string, noteMs: number): void {
  const w = Math.min(304, line.length * 8 + 12);
  const x = Math.floor(160 - w / 2);
  screen.box(x, 157, w, 13, FAINT);
  const good = note === 'COPIED!' || note === 'SIGNED!';
  if (note && noteMs < 1400) screen.text(note, 160, 160, good ? GOOD : BAD, 'center');
  else screen.text(line, 160, 160, ACCENT, 'center');
}

/** The prompt to sign the board. */
export function drawSignPanel(screen: Screen, status: string, ms: number): void {
  const r = { x: 64, y: 54, w: 192, h: 92 };
  screen.fill(r.x + 3, r.y + 3, r.w, r.h, 0);
  drawPanel(screen, r, MUTED, SELECTED);
  screen.text('SIGN THE BOARD', 160, r.y + 8, SELECTED, 'center');
  if (status) {
    screen.text(status, 160, r.y + 46, blink(ms, 700) ? INK : MUTED, 'center');
    return;
  }
  drawSpinner(screen, r.x + 10, r.y + 8, ms);
  screen.text('▲▼ ◄► OR TYPE', 160, r.y + 70, FAINT, 'center');
  screen.text('ENTER SENDS  ESC SKIPS', 160, r.y + 80, FAINT, 'center');
}
